/**
 * [TD-47.2a] RosterPanel - unified roster view for both auction + snake.
 *
 * Reads `draftState.managers[user.id].roster` (canonical engine shape;
 * both engines emit `{player, paid, ...}` entries) and renders the user's
 * drafted players grouped by position slot. Renders the "YOU NEED" badges
 * from `draftState.position_needs[user.id]` (Gap C verdict C.1: server-
 * broadcast, single source of truth).
 *
 * Format-agnostic. Shipped in TD-47.2a; consumed by auction immediately
 * and by snake in TD-47.2b.
 *
 * Props:
 *   draftState  - broadcast snapshot (must contain managers + position_needs)
 *   userId      - current user id (matches a managers key)
 *   testIdPrefix - defaults to "roster-panel"
 */
import { Users } from "lucide-react";
import PositionBadge from "../PositionBadge";
import { useDraftFilters } from "../../contexts/DraftFiltersContext";

// Position slots displayed; FLEX rendered separately at the bottom.
const STARTER_POSITION_ORDER = ["QB", "RB", "WR", "TE", "K", "DEF"];

function NeedsBadges({ needs, testIdPrefix, onChipClick }) {
  if (!needs || typeof needs !== "object") return null;

  const order = [...STARTER_POSITION_ORDER, "FLEX"];
  const unmet = order.filter((pos) => (needs[pos] || 0) > 0);

  if (unmet.length === 0) {
    return (
      <div
        className="flex items-center gap-1.5"
        data-testid={`${testIdPrefix}-needs-complete`}
      >
        <span className="text-[10px] font-display font-black uppercase tracking-wider text-[#00C853]">
          Starters Filled ✓
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid={`${testIdPrefix}-needs`}
    >
      <span className="text-[10px] font-display font-black uppercase tracking-wider text-[rgba(255,255,255,0.50)]">
        You Need:
      </span>
      {unmet.map((pos) => (
        <button
          key={pos}
          onClick={() => onChipClick?.(pos)}
          data-testid={`${testIdPrefix}-need-${pos}`}
          title={`Filter pool to ${pos}`}
          className="text-[10px] font-display font-black uppercase tracking-wider text-[#FF6B00] bg-[#FF6B00]/15 border border-[#FF6B00]/30 px-1.5 py-0.5 rounded hover:bg-[#FF6B00]/25 hover:border-[#FF6B00]/50 transition-colors cursor-pointer"
        >
          {needs[pos]} {pos}
        </button>
      ))}
    </div>
  );
}

export default function RosterPanel({
  draftState,
  userId,
  testIdPrefix = "roster-panel",
}) {
  const { setPosFilter } = useDraftFilters();
  const manager = userId ? draftState?.managers?.[userId] : null;
  const roster = manager?.roster || [];
  const needs = draftState?.position_needs?.[userId] || null;

  // [TD-47.2b Gap F.2] YOU NEED chip click → publish to DraftFiltersContext.
  // FLEX is informational only - pool can't filter to FLEX directly, so map
  // FLEX clicks to "ALL" (no-op for the pool but visually selects the chip).
  const handleNeedChipClick = (pos) => {
    setPosFilter(pos === "FLEX" ? "ALL" : pos);
  };

  // Group roster by position; preserve draft order within group.
  const grouped = STARTER_POSITION_ORDER.reduce((acc, pos) => {
    acc[pos] = roster.filter((e) => (e?.player?.position || "") === pos);
    return acc;
  }, {});

  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid={testIdPrefix}
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-[#252525] shrink-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-[#FF6B00]" />
          <span className="font-display font-black text-xs uppercase tracking-wider text-[#FF6B00]">
            Your Roster
          </span>
          <span className="font-mono text-[10px] text-[rgba(255,255,255,0.40)]">
            {roster.length}
          </span>
        </div>
      </div>

      {/* Position-need badges */}
      <div className="px-3 py-2 border-b border-[#252525] shrink-0">
        <NeedsBadges
          needs={needs}
          testIdPrefix={testIdPrefix}
          onChipClick={handleNeedChipClick}
        />
      </div>

      {/* Roster grouped by position */}
      <div className="flex-1 overflow-y-auto px-2 py-1 space-y-1.5">
        {STARTER_POSITION_ORDER.map((pos) => {
          const group = grouped[pos];
          if (!group || group.length === 0) return null;
          return (
            <div key={pos} data-testid={`${testIdPrefix}-group-${pos}`}>
              <div className="flex items-center gap-1.5 px-1 mb-0.5">
                <PositionBadge position={pos} size="sm" />
                <span className="text-[9px] font-display font-bold uppercase tracking-wider text-[rgba(255,255,255,0.35)]">
                  {pos} ({group.length})
                </span>
              </div>
              {group.map((entry, idx) => {
                const p = entry?.player || {};
                return (
                  <div
                    key={`${pos}-${p.id || idx}`}
                    className="flex items-center justify-between px-2 py-1 rounded hover:bg-[#252525]/50 transition-colors"
                    data-testid={`${testIdPrefix}-row-${p.id || `${pos}-${idx}`}`}
                  >
                    <p className="font-body text-xs text-[#F5F5F5] truncate font-medium">
                      {p.name || "-"}
                    </p>
                    {entry?.paid != null && (
                      <span className="font-mono text-[10px] text-[#FFD700] shrink-0 ml-2">
                        ${entry.paid}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {roster.length === 0 && (
          <div
            className="text-center px-3 py-4"
            data-testid={`${testIdPrefix}-empty`}
          >
            <p className="font-body text-[11px] text-[rgba(255,255,255,0.35)]">
              No picks yet - your roster builds here as you draft.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
